import axiosInstance from "../middlewares/axiosInstance";
import { AxiosResponse } from "axios";

interface BusinessStats {
  totalOrders: number;
  totalRevenue: number;
  activeUsers: number;
  lastOrderDate: Date;
}

interface SystemStats {
  totalBusinesses: number;
  activeBusinesses: number;
  totalRevenue: number;
  newBusinessesThisMonth: number;
}

interface BusinessFormData {
  business_name: string;
  owner: {
    email: string;
    first_name: string;
    last_name: string;
    phone: string;
    password: string;
  };
  contact: {
    address: string;
    city: string;
    state: string;
    country: string;
    postal_code: string;
  };
  subscription: {
    plan: string;
    features: {
      max_users: number;
      max_products: number;
      max_locations: number;
      has_inventory: boolean;
      has_analytics: boolean;
      has_customer_loyalty: boolean;
    };
  };
  settings: {
    timezone: string;
    language: string;
    currency: string;
    tax_settings: {
      tax_included: boolean;
      default_tax_rate: number;
    };
  };
}

interface Permission {
  _id?: string;
  permission_code: string;
  module: string;
  group: string;
  action: string;
  description?: string;
  is_active: boolean;
  created_at?: string;
  created_by?: {
    name: string;
    email: string;
  };
}

interface CreatePermissionRequest {
  permission_code: string;
  module: string;
  group: string;
  action: string;
  description?: string;
}

interface UpdatePermissionRequest {
  _id: string;
  permission_code?: string;
  module?: string;
  group?: string;
  action?: string;
  description?: string;
  is_active?: boolean;
}

interface ListPermissionsRequest {
  page?: number;
  limit?: number;
  search?: string;
  module?: string;
  group?: string;
  action?: string;
  is_active?: boolean;
}

class SuperAdminService {
  private readonly baseUrl = "/super-admin";

  // Dashboard Statistics
  async getSystemStats(): Promise<AxiosResponse<SystemStats>> {
    return axiosInstance.post(`${this.baseUrl}/stats`);
  }

  async getBusinessStats(
    businessId: string
  ): Promise<AxiosResponse<BusinessStats>> {
    return axiosInstance.post(`${this.baseUrl}/businesses/${businessId}/stats`);
  }

  // Business Management
  async getBusinesses(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: "active" | "suspended" | "all";
  }): Promise<AxiosResponse<any>> {
    return axiosInstance.post(`${this.baseUrl}/businesses/list`, params);
  }

  async createBusiness(data: BusinessFormData): Promise<AxiosResponse<any>> {
    // Log incoming data
    console.log(
      "Creating business with input data:",
      JSON.stringify(data, null, 2)
    );

    const payload = {
      ...data,
      is_active: true,
      is_deleted: false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      subscription: {
        ...data.subscription,
        status: "active",
        start_date: new Date().toISOString(),
        end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days from now
      },
      stats: {
        active_users: 1, // Just the owner initially
        total_orders: 0,
        total_revenue: 0,
        last_order_date: null,
      },
    };

    // Log final payload
    console.log("Sending payload to API:", JSON.stringify(payload, null, 2));

    return axiosInstance.post(`${this.baseUrl}/businesses/create`, payload);
  }

  async suspendBusiness(businessId: string): Promise<AxiosResponse<any>> {
    return axiosInstance.post(
      `${this.baseUrl}/businesses/${businessId}/suspend`
    );
  }

  async activateBusiness(businessId: string): Promise<AxiosResponse<any>> {
    return axiosInstance.post(
      `${this.baseUrl}/businesses/${businessId}/activate`
    );
  }

  // System Settings
  async getSystemSettings(): Promise<AxiosResponse<any>> {
    return axiosInstance.post(`${this.baseUrl}/settings/get`);
  }

  async updateSystemSettings(settings: any): Promise<AxiosResponse<any>> {
    return axiosInstance.post(`${this.baseUrl}/settings/update`, settings);
  }

  // Audit Logs
  async getAuditLogs(params?: {
    page?: number;
    limit?: number;
    businessId?: string;
    action?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<AxiosResponse<any>> {
    return axiosInstance.post(`${this.baseUrl}/audit-logs/list`, params);
  }

  // Permissions Management
  async createPermission(
    data: CreatePermissionRequest
  ): Promise<AxiosResponse<any>> {
    return axiosInstance.post(`${this.baseUrl}/permissions/create`, data);
  }

  async updatePermission(
    data: UpdatePermissionRequest
  ): Promise<AxiosResponse<any>> {
    return axiosInstance.post(`${this.baseUrl}/permissions/update`, data);
  }

  async listPermissions(
    params?: ListPermissionsRequest
  ): Promise<AxiosResponse<any>> {
    return axiosInstance.post(`${this.baseUrl}/permissions/list`, params);
  }

  async getPermission(id: string): Promise<AxiosResponse<any>> {
    return axiosInstance.post(`${this.baseUrl}/permissions/get`, { _id: id });
  }

  async deletePermission(id: string): Promise<AxiosResponse<any>> {
    return axiosInstance.post(`${this.baseUrl}/permissions/delete`, {
      _id: id,
    });
  }
}

export const superAdminService = new SuperAdminService();

// Export types for use in components
export type {
  Permission,
  CreatePermissionRequest,
  UpdatePermissionRequest,
  ListPermissionsRequest,
};
